import {
  bindBootAccount,
  captureBootSignal,
  getBootSnapshot,
  recoverBoot,
  retiresCapturedWriter
} from '#~/routes/root/boot-budget.js';
/* eslint-disable import/no-internal-modules */

/* eslint-disable @typescript-eslint/prefer-reduce-type-parameter */
// route-aware room selection: import fetchRoomById from the specific registry module (where it is
// Spec 4 (D1): per-run attribution for the pre-root hydration lane.
import { markRendererMilestone } from '#~/boot/renderer-startup.js';
import {
  SIGN_OUT_CAUSE,
  markSignOutCause,
  wasSignOutTracked
} from '#~/analytics/domains/sign-out-lifecycle.js';
// defined) rather than the api/agent barrel — the barrel drags extra transitive
// deps (auth-journey, error) into the synchronizer test graph and breaks the
// existing unstable_mockModule factories. registry.js is already imported here.
import { fetchRoomById, listOrganizations, listRooms, loadMembers } from '#~/api/agent/registry.js';
import { toIdFromHex } from '#~/api/utils.js';
import { config } from '#~/config/config.js';
import { useDepot } from '#~/depot/hooks.js';
import { isElectronApp } from '#~/electron/utils.js';
import { OfflineError, StudioError } from '#~/error/index.js';
import * as persistenceLayer from '#~/persistence/index.js';
import { withTimeout } from '#~/lib/with-timeout.js';
import { isTransientNetworkError } from '#~/api/transient-retry.js';
import { retryWhenReachable } from '#~/boot/retry-when-reachable.js';
import { timeBootHop } from '#~/logger/forward.js';
import { useIntercomSync } from '#~/intercom/index.js';
import { useOrgGroupIdentify, useStudioGroupIdentify } from '#~/posthog/hooks/index.js';
import { PostHogManager } from '#~/posthog/index.js';
import { useFeatureFlag } from '#~/posthog/hooks/use-feature-flag.js';
import { authStore, type RegistryList, type RegistryListState } from './store.js';
import { seedLocalStorage } from '#~/preferences/preferences.js';
import { useLastRoomIdPref } from '#~/room/hook_last_id.js';
import { Paths } from '#~/routes/constants.js';
import { BootErrorScreen } from '#~/routes/boot-overlay/boot-error-screen.js';
import { isDesktopSessionHeld } from './desktop-session.js';
import { speculativeLiveKitPreConnect } from './speculative-preconnect.js';
import { Loader } from '@studio/design-system/loader';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { castId } from '@studio/models';
import type { Account } from '@studio/models/account';
import type { Organization } from '@studio/models/organization';
import type { RoomRecord } from '@studio/models/room';
import type { User } from '@studio/models/user';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import type { Props } from './types.js';
import type { User as Auth0User } from '@auth0/auth0-react';
import { useAuth0 } from './use-auth0.js';
import { isSessionEndedError } from './session-return.js';
import { resolveActiveRoomId, shouldOverwriteLastRoomId } from './room-selection.js';
import { extractOrganizationData, fail, fetchStudioUser, toStudioUser } from './utils.js';
import {
  mergeHydratedBootstrapRoomRecords,
  toOrganizationMap,
  toRoomRecordMap
} from './registry-transformers.js';
import { awaitOrganizationVisibility, completeRoomOrganizations } from './registry-completeness.js';
import {
  getSignupIdentitySetOnce,
  maybeTrackRegistrationCompleted,
  setLastKnownUserId
} from './auth-journey.js';

/** User accessor. */
// Spec 4 (D1): distinguishes repeated runs of the synchronizer effect WITHIN one
// document. Module scope, not a ref: this file's tests drive the effect outside a
// full renderer, and an extra hook call would change that harness.
let synchronizerRunSequence = 0;

export const CurrentUser: { user: T_Maybe<User> } = { user: undefined };

// ENG-4034: hard deadline on the two blocking auth/registry awaits below. This
// gate renders `<Loader/>` while `isAuthenticated && !hasStudioUser`, sits above
// `<Root>` (so the 17s TimeoutWatchdog has not mounted), and only leaves the
// loader on resolve OR throw. A hung request (slow/partitioned network on a
// first boot with no cached user) therefore blanks forever — the reproduced
// WSOD. Timing out makes the await REJECT, routing into the existing catch
// (Auth0-only fallback / error boundary) instead. 15s is generous vs the ~6.5s
// used for bridge:get-id (electron/api.ts) but the auth0 + mailroom round-trips
// are slower and we prefer a real boot over a premature fallback on a merely
// slow link.
const AUTH_GATE_TIMEOUT_MS = 15_000;

// route-aware room selection: bounded read-after-write reconcile for the active room. The
// embedded-camera link flow creates a room and navigates straight to
// /room/:roomId(/boot), but the boot-time listRooms can miss that just-created
// room (registry replica lag) — so its record (with permissions) is absent from
// the depot, the room renders in VIEWER mode, and the UI shows a stale "last"
// studio. We fetch the missing room directly a few times before giving up.
// Primary-backed bootstrap can route before the registry replica exposes the
// room's permissions and linked agents. Keep polling within the existing boot
// budget, then fail explicitly instead of leaving an agentless placeholder on
// /boot forever. Per e2e-tests-fail-fast-pass-fast + settle-every-terminal-branch
// (ai.knowledge/global).
const ACTIVE_ROOM_RECONCILE_ATTEMPTS = 20;
const ACTIVE_ROOM_RECONCILE_DELAY_MS = 500;

/** `<ProfileSynchronizer>` component. */
export const ProfileSynchronizer = ({ children }: Pick<Props, 'children'>): ReactElement => {
  const { error: auth0Error, isAuthenticated, isLoading, user: auth0User } = useAuth0();
  const { enabled: n3Enabled } = useFeatureFlag('alert_n3_enabled', { defaultValue: true });
  const n3EnabledRef = useRef(n3Enabled);
  n3EnabledRef.current = n3Enabled;
  useEffect(() => {
    authStore.getState().clearRegistryLists();
    return () => authStore.getState().clearRegistryLists();
  }, [auth0User?.sub, isAuthenticated]);
  const [hasStudioUser, produce] = useDepot((µ) => [!!µ.user, µ.produce], shallow);
  const bootError = useDepot((µ) => µ.boot.error);
  const [studioError, setStudioError] = useState<T_Maybe<Error>>();
  const preConnectAbortRef = useRef<AbortController | null>(null);
  // Spec 4 (D3): `run()` is fire-and-forget and its cleanup does not cancel it,
  // so several runs can be in flight at once (the effect re-enters on route,
  // identity and preference-load changes). Ownership is monotonic: the NEWEST
  // run owns the depot, and every write below is guarded on still being that
  // run.
  //
  // Monotonic on purpose. Keying the guard on (account, route) instead would let
  // an A -> B -> A sequence resurrect the first run's writes, and would let a
  // stale same-scope FAILURE overwrite a newer success. A superseded run losing
  // its write is not a loss of function: the newer run does the same reads and
  // publishes them itself.
  const runGenerationRef = useRef(0);

  const [currentRoomId, setLastRoomIdPref, hasLoadedLastRoomIdPref] = useLastRoomIdPref();

  // run() both reads the last-room preference and writes it back. The read goes
  // through a ref and the write through a stable wrapper so that write cannot
  // re-enter this effect: under monotonic ownership a self-triggered re-entry
  // would supersede the run that already holds the census and members, and the
  // successor would pay a second full registry round to publish the same data.
  const currentRoomIdRef = useRef(currentRoomId);
  currentRoomIdRef.current = currentRoomId;
  const writeLastRoomIdPref = useLatestFn(setLastRoomIdPref);

  // route-aware room selection: the room the user is actually viewing, parsed from the URL. The
  // splat matches both /room/:roomId and /room/:roomId/boot. Used to make the
  // active route authoritative over a stale boot-time room list (read-after-write
  // lag from the just-created room). undefined on the index / non-room routes,
  // where the existing bootstrap selection is unchanged.
  const roomRouteMatch = useMatch('/room/:roomId/*');
  const activeRoomRouteId = roomRouteMatch?.params.roomId as T_Maybe<RoomRecord['id']>;

  if (auth0Error) {
    throw fail('734E', auth0Error);
  }

  if (studioError) {
    throw fail('AC93', studioError);
  }

  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated || !hasLoadedLastRoomIdPref) {
      if (!isAuthenticated) {
        if (!isLoading) bindBootAccount(null);
        clearCurrentUser();
      }

      return;
    }

    // A navigation/account change retires this writer. Late responses must
    // neither publish the previous account nor settle the new writer's flag.
    bindBootAccount(auth0User?.sub ?? null);
    const launchSignal = captureBootSignal();
    let cancelled = launchSignal.aborted;
    // Set when a WAN-class boot failure arms the reachability retry below.
    let stopAutoRetry: (() => void) | undefined;
    // Publish a retryable boot error. Try Again does a clean reboot-like restart
    // (clear boot.error + recoverBoot/reload), never a re-entry of run().
    // `autoRetry` is for failures caused only by an unreachable cloud (registry
    // reads timed out / could not connect): those retry by themselves once the
    // API answers again. With the LAN still up no `online` event fires, so
    // without it Studio sat on this screen after every outage until someone
    // clicked Try Again after a relaunch or renderer reload during the outage.
    // Per rule: reference-rules-in-comments (ai.knowledge/global).
    const publishRetryableBootError = (message: string, detail: string, autoRetry: boolean) => {
      let retried = false;
      const retryFn = (): void => {
        if (retried) return;
        retried = true;
        stopAutoRetry?.();
        produce((d) => {
          d.boot.error = null;
        });
        if (getBootSnapshot()) void recoverBoot('retry');
        else window.location.reload();
      };
      produce((draft) => {
        draft.boot.error = { message, detail, severity: 'retryable', retryFn };
      });
      if (autoRetry) stopAutoRetry = retryWhenReachable(retryFn);
    };
    const organizationRecovery = new AbortController();
    const attempt = getBootSnapshot()?.attemptId;
    const retire = () => {
      cancelled = true;
      organizationRecovery.abort();
    };
    // The launch signal fires for TWO different things, and only one of them
    // means this writer is obsolete.
    //
    // It fires when a newer account/attempt takes over -- the case the comment
    // above is about. It ALSO fires the moment the launch settles, including a
    // SUCCESSFUL one: boot-budget's `receive` aborts the generation on the first
    // non-null outcome, by design ("retiring boot work at readiness must not
    // cancel later ordinary registry refreshes"). But this writer is not a later
    // refresh -- readiness is published while it is still at
    // `loading-organizations` (measured: boot_succeeded at 20988ms with that
    // stage), so retiring on success kills the organization hydration that is
    // legitimately still in flight. registry-completeness/bootstrap-first then
    // sees ONE hydration round where `main` sees two; `main` has no fencing here
    // at all, so this is a regression of the fencing, not of the registry.
    //
    // A ready launch is not a newer launch. Only an attempt change or a
    // failed/cancelled one retires the writer -- and a genuine account change is
    // covered regardless, because this effect lists `auth0User` in its
    // dependencies and its cleanup below sets `cancelled` on every re-run.
    // Per rule: reference-rules-in-comments (ai.knowledge/global).
    const retireUnlessReady = (): void => {
      if (retiresCapturedWriter(attempt, getBootSnapshot())) retire();
    };
    launchSignal.addEventListener('abort', retireUnlessReady, { once: true });
    const run = async (): Promise<void> => {
      if (cancelled) return;

      // This effect re-enters within one document on route, identity and
      // preference-load changes. A per-run lane keeps repeats separable instead
      // of collapsing several registry loads into one bucket.
      synchronizerRunSequence += 1;
      const runLane = `synchronizer:${synchronizerRunSequence}`;
      markRendererMilestone('synchronizer.run-started');

      runGenerationRef.current += 1;
      const generation = runGenerationRef.current;
      const isLatestRun = (): boolean => runGenerationRef.current === generation;

      produce((draft) => {
        draft.synchronizer.isRegistryLoading = true;
      });

      try {
        if (!auth0User) {
          const error = new StudioError('Unable to decode OAuth2 user.');
          setStudioError(error);

          produce((draft) => {
            draft.user = undefined;
            draft.synchronizer.hasRegistryLoaded = false;
          });

          return;
        }

        // ENG-4034: both awaits are timeout-guarded. A hang here (not a reject)
        // is what blanks the shell; on timeout `withTimeout` rejects and the
        // catch below falls back to the Auth0-only user.
        // seedLocalStorage has no auth dependency — run it in parallel with the
        // token fetch (the seed is unguarded; only the auth await needs the gate).
        const [token] = await Promise.all([
          timeBootHop(
            'synchronizer.getAccessToken',
            withTimeout(getAccessTokenSilently(), AUTH_GATE_TIMEOUT_MS, 'getAccessTokenSilently'),
            runLane
          ),
          // Spec 4 (D1): the seed shares this Promise.all, so a slow seed delays
          // the token group even though it has no auth dependency. Measured for
          // exactly that reason (review-4 §4, persistence row).
          timeBootHop('synchronizer.seedLocalStorage', seedLocalStorage(), runLane)
        ]);
        if (cancelled) return;

        if (!isLatestRun()) {
          // Superseded during the token wait: neither the logout redirect nor a
          // pre-connect swap belongs to this run any more (it would abort the
          // newer run's warm-up and replace it with its own).
          return;
        }

        if (token === null) {
          // On desktop a null token also means "expired and Auth0 unreachable"
          // (WAN down in a local mode session), while main still holds the
          // session. Redirecting to /logout there would wipe a working session,
          // so only a session main no longer holds signs out; otherwise this
          // surfaces as the same retryable boot error as a token-fetch throw.
          if (isElectronApp() && (await isDesktopSessionHeld())) {
            throw new OfflineError();
          }
          if (cancelled || !isLatestRun()) return;
          // A fatal-refresh IPC may already have recorded sign-out and started
          // the entry reload before this token reply or React cleanup arrives.
          if (wasSignOutTracked()) return;

          if (authStore.getState().sessionEnded) return;
          // ENG-4874: no held session remains, so the session ended on its
          // own. The /logout route is a full navigation; leave it the cause.
          markSignOutCause(SIGN_OUT_CAUSE.SESSION_ENDED);
          window.location.href = `/${Paths.logout}`;
          return;
        }

        // Speculative LiveKit pre-connect: warm TCP+TLS while we fetch user/rooms.
        // Abort if the resolved room changes after registry loads.
        const preConnectRoomId = currentRoomIdRef.current;
        if (preConnectRoomId) {
          preConnectAbortRef.current?.abort();
          const controller = new AbortController();
          preConnectAbortRef.current = controller;
          speculativeLiveKitPreConnect(preConnectRoomId, controller.signal);
        }

        // The studio-user, rooms, and organizations fetches are independent —
        // each needs only the token, none needs the resolved user — so they run
        // concurrently in the single Promise.all below (see the parallel fetch).
        // This is what keeps the blocked-network error path from stacking two
        // serial transport-retry exhausts.

        // Attempt to fetch rooms and organizations from registry.
        // If these fail (e.g., NATS not connected), we'll proceed with empty lists.
        // The bounded transient retry now lives ONE LAYER DOWN in the transport
        // (api/agent/registry.ts withTransientRetry: a single "Failed to fetch"
        // TypeError or 5xx on rapid refresh is absorbed there). The redundant
        // call-site pRetry was removed so the two layers do not stack (was
        // 4x*4x). These .catch() arms and the roomsFailed flag are UNCHANGED —
        // .catch() now fires only AFTER the transport retries are exhausted (a
        // genuine sustained failure), so the B2 guard below still reflects real
        // offline. A 403 is NOT retried at the transport (cross-account is owned
        // by the auto-link re-probe), so it still surfaces immediately here.
        // Per rule: reference-rules-in-comments + think-in-systems (ai.knowledge/global).
        let roomsFailed = false;
        let organizationsFailed = false;
        let roomsAlertEligible = false;
        let organizationsAlertEligible = false;

        // perf/boot-latency E2E STEER: the shared e2e Auth0 account
        // accumulates one org per CI run (global-setup provisionPerRunOrg),
        // so the unscoped listRooms fans out to EVERY accumulated org's
        // rooms — measured at a repeatable ~4s per call (IRIS_DIAG_BOOT_HOP,
        // e2e-mac run 29791952251) vs ~150ms for the org-scoped query. When
        // the harness has pinned a per-run org via the @@ls.e2eAutoLinkOrgId
        // persistence key, scope the boot listRooms to THAT org — every room
        // the run touches is created in it (see the identical steer in
        // auto-link-room.ts createAutoLinkRoom, the precedent this follows).
        // Production is untouched: the key exists only in e2e stores, and the
        // envelope (Electron + iris vendor) matches the auto-link steer.
        // Per rule: reference-rules-in-comments + stay-in-scope
        // (ai.knowledge/global).
        let e2ePinnedOrgId: Uint8Array | undefined;
        if (isElectronApp() && config.vendor === 'iris') {
          const e2eOrgHex = await timeBootHop(
            'synchronizer.e2eOrgSteerRead',
            persistenceLayer.get('e2eAutoLinkOrgId'),
            runLane
          ).catch((steerError) => {
            // Observability, not control flow: a failed read falls back to the
            // unscoped call, but the failure must be visible in the CI job log
            // (run 29794760929 showed the steer silently not engaging).
            console.warn('[synchronizer] e2e org-steer persistence read failed:', steerError);
            return undefined;
          });
          if (cancelled) return;
          if (e2eOrgHex) {
            e2ePinnedOrgId = toIdFromHex(e2eOrgHex) ?? undefined;
          }
        }
        // Diagnostic: 1 = org-scoped listRooms engaged, 0 = unscoped. Read
        // next to synchronizer.listRooms/.count to attribute its cost
        // (bloated-account work vs Lambda cold start).
        console.log(
          `IRIS_DIAG_BOOT_HOP name=synchronizer.listRooms.steer ms=${e2ePinnedOrgId ? 1 : 0} ok=true`
        );

        // fetchStudioUser is folded into this Promise.all: it is
        // timeout-guarded (a true hang still rejects into the Auth0-only
        // fallback catch) and shares nothing with the room/org calls but the
        // token. Running all three together means a blocked network exhausts
        // their transport retries in parallel (~5s) instead of serially (~11s),
        // which is what the boot-error tests' ceilings are sized against.
        // Spec 4 (D3). The unscoped room list is STARTED here but no longer
        // awaited before the active room can be published. Measured on the
        // reference account (REPORT-D2): this one call costs ~3.9s of server
        // time and, because the overlay's registry dimension waited on it, it
        // owned ~85% of the time to first usable render — while the room route
        // itself only needs ONE room's record. Its promise, its catch and its
        // roomsFailed semantics are unchanged; only the await moved.
        const roomsPromise = timeBootHop(
          'synchronizer.listRooms',
          listRooms(token, e2ePinnedOrgId),
          runLane
        ).catch((error) => {
          console.warn('Unable to fetch rooms after retries, proceeding without room data:', error);
          roomsFailed = true;
          roomsAlertEligible = n3EnabledRef.current === true;
          return [] as Awaited<ReturnType<typeof listRooms>>;
        });

        // One targeted read for the room the user is actually on, concurrent
        // with the user/org reads (~250ms against the same registry). Only on a
        // room route: on the index there is no known room, and every existing
        // selection/bootstrap path stays exactly as it was.
        const activeRoomPromise = activeRoomRouteId
          ? timeBootHop(
              'synchronizer.fetchActiveRoom',
              fetchRoomById(activeRoomRouteId, token),
              runLane
            ).catch((error) => {
              // Best-effort: a failure here simply falls back to today's
              // behaviour of waiting for the full list. A 403/404 must NOT
              // shortcut the overlay, which is exactly what returning undefined
              // achieves.
              console.warn('Studio: synchronizer: early active-room fetch failed', error);
              return undefined;
            })
          : Promise.resolve(undefined);

        // MERGE DECISION (ENG-4720 x ENG-4722): the targeted read above and the
        // read-after-write reconcile loop below were both asking the registry
        // for the SAME room on a room-route boot — one duplicate network read
        // per boot, on exactly the path this ticket exists to make faster. They
        // are one read now: when the early fetch has already answered, it IS the
        // loop's first attempt.
        //
        // Recorded WITHOUT awaiting, because the early read is deliberately
        // opportunistic (see the publish block below). If it is still in flight
        // when the room list arrives, the loop issues its own fetch and behaves
        // exactly as it did before — a boot must never be made slower by the
        // thing meant to make it faster.
        let earlyActiveRoom: Awaited<typeof activeRoomPromise>;
        let earlyActiveRoomSettled = false;
        void activeRoomPromise.then((room) => {
          earlyActiveRoom = room;
          earlyActiveRoomSettled = true;
        });

        const [{ user, didFallback }, organizations] = await Promise.all([
          timeBootHop(
            'synchronizer.fetchStudioUser',
            withTimeout(fetchStudioUser(auth0User, token), AUTH_GATE_TIMEOUT_MS, 'fetchStudioUser'),
            runLane
          ),
          timeBootHop('synchronizer.listOrganizations', listOrganizations(token), runLane).catch(
            (error) => {
              organizationsFailed = true;
              organizationsAlertEligible = n3EnabledRef.current === true;
              console.warn(
                'Unable to fetch organizations after retries, proceeding without org data:',
                error
              );
              return [] as Awaited<ReturnType<typeof listOrganizations>>;
            }
          )
        ]);
        if (cancelled) return;

        if (!isLatestRun()) {
          // Superseded (account switch, route change, logout): this run's user
          // must not land on top of a newer run's.
          return;
        }

        // Identity side effects (PostHog identify, last-known-user id,
        // CurrentUser) are global and durable, so only the owning run fires them.
        // A null user is a hard boot failure; a present user hydrates the shell
        // before the room data below is consumed.
        if (user) {
          setCurrentUser(user, auth0User);
        }

        markRendererMilestone('synchronizer.user-published');
        produce((draft) => {
          // Spec 4 (D1): descendants can mount from here.
          draft.user = user;

          if (!user) {
            draft.synchronizer.hasRegistryLoaded = false;
            return;
          }

          // @fixme - this is a hack until we have real account (AKA organization) records.
          draft.account = { id: castId<Account['id']>(user.id), name: 'Iris Productions' };
        });

        if (!user) {
          setStudioError(new StudioError('Unable to fetch user.'));
          return;
        }

        // Spec 4 (D3): publish the ACTIVE room's record (and its organization)
        // as soon as the targeted read lands, WITHOUT touching
        // hasRegistryLoaded. That flag means "the full registry census is in the
        // depot" and the studio switcher reads it that way — with an incomplete
        // map it would show a one-studio account, or "Create a studio" on an
        // account that has many (switcher.tsx:255, switcher/data.ts:101). So the
        // census stays false here and is still set below, after the full list.
        //
        // `permissions.canControl` must be a real boolean before this is worth
        // publishing: page.tsx renders producer-vs-viewer chrome from it, and an
        // empty `permissions` object would paint viewer mode. A record without
        // it is simply not published early, and boot waits as it does today.
        // Deliberately NOT awaited: the targeted read is an OPPORTUNISTIC
        // accelerator. If it is slow or stalls, the full-list path below must
        // still complete exactly as fast as it does today — a boot must never be
        // made slower by the thing meant to make it faster.
        if (activeRoomRouteId) {
          void activeRoomPromise
            .then((earlyActiveRoom) => {
              if (!earlyActiveRoom || !isLatestRun()) {
                return;
              }

              const earlyRoomsMap = toRoomRecordMap([earlyActiveRoom]);
              const earlyRecord = earlyRoomsMap[activeRoomRouteId];

              if (!earlyRecord || earlyRecord.permissions.canControl === undefined) {
                return;
              }

              const earlyOrganizations = toOrganizationMap(organizations, user);

              produce((draft) => {
                // Merge, never replace: useRootBootstrap's warm path may already
                // have written this room, and the full-census write below unions
                // on top of whatever is here.
                draft.roomRecords = { ...draft.roomRecords, ...earlyRoomsMap };

                const organization = earlyOrganizations[earlyRecord.organizationId];

                if (organization && !draft.organizations[earlyRecord.organizationId]) {
                  draft.organizations[earlyRecord.organizationId] = organization;
                }
              });
              markRendererMilestone('synchronizer.active-room-published');
            })
            .catch((error) => {
              // Nothing in the callback is expected to throw; if it ever does, boot
              // must not surface an unhandled rejection over a working fallback.
              console.warn('Studio: synchronizer: early active-room publish failed', error);
            });
        }

        // From here the behaviour is unchanged — including every B2 / reconcile /
        // members / census decision — it simply happens after the overlay has
        // already been allowed to dismiss on a room route.
        const fetchedRooms = await roomsPromise;

        if (!isLatestRun()) {
          // A newer run owns the depot now; publishing this run's census would
          // overwrite it with an older account's or an older route's data.
          return;
        }

        // route-aware room selection: mutable so the read-after-write reconcile below can merge the
        // active room when a stale boot-time listRooms missed it.
        let rooms = fetchedRooms;

        // Each failed list retries only its own request. Its callback identity
        // owns recovery until a newer list load replaces it or sign-out clears
        // it. Account lists survive navigation, including after boot retires.
        const listState = (
          list: RegistryList,
          failed: boolean,
          eligible: boolean
        ): RegistryListState => {
          let retrying = false;
          const ownsRetry = (): boolean =>
            authStore.getState().registryLists[list]?.retry === retry;
          const publish = (): void => {
            if (!ownsRetry()) return;
            const current = authStore.getState().registryLists[list];
            if (current?.retry === retry && !current.failed) failed = false;
            authStore.getState().setRegistryList(list, { failed, eligible, retrying, retry });
          };
          const retry = async (): Promise<boolean> => {
            if (retrying || !ownsRetry()) return false;
            retrying = true;
            publish();
            try {
              const retryToken = await withTimeout(
                getAccessTokenSilently(),
                AUTH_GATE_TIMEOUT_MS,
                'getAccessTokenSilently'
              );
              if (!retryToken || !ownsRetry()) return false;
              if (list === 'studios') {
                const loaded = await listRooms(retryToken, e2ePinnedOrgId, { fresh: true });
                if (!ownsRetry()) return false;
                produce((draft) => {
                  draft.roomRecords = toRoomRecordMap(loaded);
                });
              } else {
                const loaded = await listOrganizations(retryToken, { fresh: true });
                if (!ownsRetry()) return false;
                produce((draft) => {
                  draft.organizations = completeRoomOrganizations(
                    draft.roomRecords,
                    toOrganizationMap(loaded, user),
                    draft.organizations
                  );
                });
              }
              failed = false;
              return true;
            } catch {
              return false;
            } finally {
              retrying = false;
              publish();
            }
          };
          return { failed, eligible, retrying, retry };
        };
        authStore
          .getState()
          .setRegistryList('studios', listState('studios', roomsFailed, roomsAlertEligible));
        authStore
          .getState()
          .setRegistryList(
            'organizations',
            listState('organizations', organizationsFailed, organizationsAlertEligible)
          );

        // Boot-hop diagnostics: the listRooms hop's cost scales with the row
        // count (bloated shared-e2e accounts), so emit the count next to the
        // timing line — this is what distinguishes "slow because cold start"
        // from "slow because the account drags thousands of rooms".
        console.log(
          `IRIS_DIAG_BOOT_HOP name=synchronizer.listRooms.count ms=${rooms.length} ok=true`
        );

        // B2 fix: empty rooms is only a boot error when the rooms data is
        // unreliable (listRooms threw), or on Electron when the user fetch fell
        // back to Auth0-only data (a degraded user there feeds the
        // ensureDefaultOrgAndRoom bootstrap, which mints junk/duplicate rooms —
        // ENG-4154). On web, a SUCCESSFUL empty listRooms is authoritative: it
        // is the normal state for a brand-new account, which must proceed so
        // useRootBootstrap can redirect to /agent/setup. Gating on didFallback
        // alone here blocked every new web signup behind "Unable to connect".
        // Try-Again does a clean reboot-like restart (clear boot.error +
        // window.location.reload) instead of re-entering run() unawaited. The
        // recursive re-entry stacked overlapping boot attempts and surfaced
        // uncaught rejections → app lock-up/crash. Mirrors the robust reboot
        // path at agent/provider/utils.ts:78.
        if (
          rooms.length === 0 &&
          // Only the picker replaces this error with an in-place N3 retry.
          ((roomsFailed && (!roomsAlertEligible || !!activeRoomRouteId)) ||
            (didFallback && isElectronApp()))
        ) {
          publishRetryableBootError(
            'Unable to connect. Please check your internet connection.',
            'Studio API unreachable — using cached credentials',
            true
          );
          return;
        }

        // Web new-account path: keep the lost error signal observable. The
        // fallback user is sufficient for the /agent/setup flow (pre-B2 behavior).
        if (rooms.length === 0 && didFallback) {
          console.warn(
            'Studio: user registry fetch fell back to Auth0-only data with zero rooms — proceeding to setup (new-account path)'
          );
        }

        let roomsMap = toRoomRecordMap(rooms);

        // route-aware room selection: read-after-write reconcile. If the user is on a specific room
        // route but that room isn't in the (possibly replica-lagged) list, fetch
        // it directly and merge — otherwise its record never lands in the depot
        // and the room renders in viewer mode / as the wrong studio. Gated on an
        // active room route, so the index/bootstrap auto-link and duplicate-room
        // paths are byte-for-byte unchanged. Per rule:
        // reference-rules-in-comments + think-in-systems (ai.knowledge/global).
        if (activeRoomRouteId && !roomsMap[activeRoomRouteId]) {
          for (let attempt = 0; attempt < ACTIVE_ROOM_RECONCILE_ATTEMPTS; attempt++) {
            try {
              // A settled early read is this attempt: a hit ends the loop, and a
              // miss spends the attempt rather than repeating, in the same tick,
              // a question the registry has already answered.
              const room =
                attempt === 0 && earlyActiveRoomSettled
                  ? earlyActiveRoom
                  : await timeBootHop(
                      'synchronizer.fetchRoomById',
                      fetchRoomById(activeRoomRouteId, token),
                      runLane
                    );
              if (cancelled) return;

              if (room) {
                rooms = [...rooms, room];
                roomsMap = toRoomRecordMap(rooms);
                break;
              }
            } catch (error) {
              if (cancelled) return;
              console.warn('Studio: synchronizer: active-room reconcile fetch failed', error);
            }

            // The just-created room may not be on the read replica yet — brief
            // backoff before the next attempt. Do not pay an unused delay after
            // the terminal attempt, and never publish for a run cancelled while
            // the delay was pending.
            if (attempt + 1 < ACTIVE_ROOM_RECONCILE_ATTEMPTS) {
              await new Promise((resolve) => setTimeout(resolve, ACTIVE_ROOM_RECONCILE_DELAY_MS));
              if (cancelled) return;
            }
          }
        }

        if (!isLatestRun()) {
          // The reconcile loop above awaits (and sleeps between attempts), so a
          // newer run can take ownership while it runs. Everything below this
          // point has side effects — preference writes, the speculative-connect
          // abort, member publication — so the check belongs here, not only at
          // the census write.
          return;
        }

        const organizationsMap = toOrganizationMap(organizations, user);

        const lastRoomId = currentRoomIdRef.current;
        const resolvedRoomId = resolveActiveRoomId(activeRoomRouteId, lastRoomId, roomsMap);

        if (shouldOverwriteLastRoomId(resolvedRoomId, lastRoomId, activeRoomRouteId, roomsMap)) {
          writeLastRoomIdPref(resolvedRoomId);
        }

        // Abort speculative pre-connect if resolved room differs from the cached one.
        if (resolvedRoomId !== lastRoomId && preConnectAbortRef.current) {
          console.info('[speculativeLiveKitPreConnect] abandoned: room-mismatch');
          preConnectAbortRef.current.abort();
          preConnectAbortRef.current = null;
        }

        const resolvedRoomRecord = resolvedRoomId ? roomsMap[resolvedRoomId] : undefined;

        if (resolvedRoomId && resolvedRoomRecord) {
          try {
            const { invites, members } = await timeBootHop(
              'synchronizer.loadMembers',
              loadMembers(resolvedRoomId, resolvedRoomRecord.organizationId, token),
              runLane
            );
            if (cancelled) return;

            if (isLatestRun()) {
              produce((draft) => {
                draft.roomInviteRecords = invites;
                draft.roomMembershipRecords = members;
              });
            }
          } catch (error) {
            if (cancelled) return;
            console.warn('Unable to load room members, proceeding without member data:', error);

            // loadMembers is awaited, so a newer run can own the depot by the
            // time this rejects. Clearing the arrays then would wipe the CURRENT
            // room's members on a failure that belongs to an abandoned run.
            if (isLatestRun()) {
              produce((draft) => {
                draft.roomInviteRecords = [];
                draft.roomMembershipRecords = [];
              });
            }
          }
        } else {
          // route-aware room selection: don't clear lastRoomId while the user is on a specific room
          // route whose record hasn't reconciled yet — room.tsx owns that pref for
          // the active room. Clearing it here flipped the studio identity to a
          // stale studio.
          if (!activeRoomRouteId && isLatestRun()) {
            writeLastRoomIdPref(undefined);
          }

          if (isLatestRun()) {
            produce((draft) => {
              draft.roomInviteRecords = [];
              draft.roomMembershipRecords = [];
            });
          }
        }

        if (!isLatestRun()) {
          return;
        }

        let missingOrganizationIds: string[] = [];
        produce((draft) => {
          // Keep the first writer's room set (including removals), but always
          // complete its missing organizations. A loaded flag alone does not
          // prove the room/org snapshot is complete.
          if (!draft.synchronizer.hasRegistryLoaded) {
            // MERGE DECISION (ENG-4720 × ENG-4722): main's assign wins over the
            // union Spec 4 (D3) had here. The union existed so the fast path's
            // early active-room record could not be dropped by a census that came
            // back without it — but the read-after-write reconcile above already
            // merges that room into `roomsMap` before this write, so the union was
            // redundant. And it was actively wrong: it resurrects rooms the census
            // legitimately removed (deleted, or access revoked), which is the one
            // thing this snapshot must honour. Organizations go through main's
            // completeRoomOrganizations, which fills only the orgs the rooms in
            // THIS snapshot still need — see multi-writer-registry-completeness.
            draft.roomRecords = roomsMap;
            draft.organizations = completeRoomOrganizations(
              roomsMap,
              organizationsMap,
              draft.organizations
            );
            draft.synchronizer.hasRegistryLoaded = true;
          } else {
            // Root may seed primary-only room identities before replicas expose
            // permissions and linked agents. Replace only those explicit
            // placeholders; never overwrite an already hydrated record.
            draft.roomRecords = mergeHydratedBootstrapRoomRecords(
              draft.roomRecords as Record<RoomRecord['id'], RoomRecord>,
              roomsMap
            );

            for (const [organizationId, organization] of Object.entries(organizationsMap)) {
              if (!draft.organizations[organizationId as Organization['id']]) {
                draft.organizations[organizationId as Organization['id']] = organization;
              }
            }
          }

          if (activeRoomRouteId && !draft.roomRecords[activeRoomRouteId]) {
            // route-aware room selection: registry already loaded with a stale list that missed the
            // active room (link-flow navigation after first boot). Merge just that
            // record — plus its organization so the switcher can resolve it — so
            // the room renders with real permissions (producer, not viewer)
            // without disturbing the rest of the loaded state.
            const activeRecord = roomsMap[activeRoomRouteId];

            if (activeRecord) {
              draft.roomRecords[activeRoomRouteId] = activeRecord;
            }
          }

          if (
            activeRoomRouteId &&
            draft.roomRecords[activeRoomRouteId]?.registryHydration ===
              'authoritative_placeholder' &&
            !roomsMap[activeRoomRouteId]
          ) {
            // A primary identity alone cannot satisfy RoomBoot: permissions and
            // linked-agent state must hydrate from the registry. Surface a
            // retryable terminal state when bounded reconciliation is exhausted
            // rather than leaving the user on an infinite loading screen.
            draft.boot.error = {
              message: 'Studio is still syncing. Please try again.',
              detail: 'Studio permissions and bridge details are not available yet.',
              severity: 'retryable',
              retryFn: () => {
                produce((d) => {
                  d.boot.error = null;
                });
                window.location.reload();
              }
            };
          }

          draft.organizations = completeRoomOrganizations(
            draft.roomRecords,
            draft.organizations,
            organizationsMap
          );
          missingOrganizationIds = [
            ...new Set(
              Object.values(draft.roomRecords)
                .map((room) => room.organizationId)
                .filter((id) => !draft.organizations[id])
            )
          ];
        });
        // Spec 4 (D1): the overlay's registry-loaded dimension clears from here.
        markRendererMilestone('synchronizer.registry-published');

        if (missingOrganizationIds.length > 0) {
          // Direct room refreshes and warm linked-room boots have no root list
          // writer. Repair this writer's own incomplete read with a fresh query,
          // while the already-published user/room snapshot keeps the shell usable.
          const organizationList = authStore.getState().registryLists.organizations;
          const recovered = await awaitOrganizationVisibility({
            organizationIds: missingOrganizationIds,
            organizations: Object.values(organizationsMap),
            listOrganizations: async () =>
              Object.values(
                toOrganizationMap(await listOrganizations(token, { fresh: true }), user)
              ),
            orgIdOf: (organization) => organization.id,
            timeoutMs: 10_000,
            pollIntervalMs: 500,
            signal: organizationRecovery.signal
          });
          if (cancelled || !isLatestRun()) return;
          const recoveredMap = Object.fromEntries(
            recovered.map((organization) => [organization.id, organization])
          );
          produce((draft) => {
            draft.organizations = completeRoomOrganizations(
              draft.roomRecords,
              draft.organizations,
              recoveredMap
            );
          });
          // A timeout can return a partial snapshot. Only complete recovery
          // resolves this failure, and never a replacement request's failure.
          if (
            missingOrganizationIds.every((id) => recoveredMap[id]) &&
            authStore.getState().registryLists.organizations?.retry === organizationList?.retry
          ) {
            authStore.getState().resolveRegistryList('organizations');
          }
        }
      } catch (error) {
        if (cancelled) return;
        if (isSessionEndedError(error) && authStore.getState().sessionEnded) return;
        console.error('@PROFILE SYNCHRONIZER ERROR: ', error);

        if (!isLatestRun()) {
          // A superseded run's failure is not the current boot's failure: after a
          // route change or account switch, publishing it would put an error
          // screen over a boot that is proceeding normally.
          return;
        }

        // Surface boot.error with retryFn. Covers B3 (getAccessTokenSilently
        // throws) and any other unexpected throw. Try-Again does a clean
        // reboot-like restart (clear boot.error + window.location.reload)
        // instead of re-entering run() unawaited — the recursive re-entry
        // stacked overlapping boot attempts and surfaced uncaught rejections
        // → app lock-up/crash. Mirrors agent/provider/utils.ts:78.
        publishRetryableBootError(
          'Something went wrong. Please try again.',
          error instanceof Error ? error.message : 'Failed to synchronize',
          // By name, not instanceof: several suites mock with-timeout without the class.
          (error instanceof Error && error.name === 'TimeoutError') ||
            // A held desktop session can refresh after reconnect without signing in again.
            error instanceof OfflineError ||
            isTransientNetworkError(error)
        );
      } finally {
        // MERGE DECISION (ENG-4720 × ENG-4722): main gates this on `cancelled`
        // (the effect was torn down); Spec 4 (D3) gates it on still owning the
        // depot. Kept BOTH. A run superseded WITHOUT a teardown — the ordinary
        // case on a studio switch — passes main's guard but must not report a
        // NEWER run's in-flight load as finished, or the switcher drops its
        // loading state early.
        if (!cancelled && isLatestRun()) {
          produce((draft) => {
            draft.synchronizer.isRegistryLoading = false;
          });
        }
      }
    };

    void run();

    return () => {
      launchSignal.removeEventListener('abort', retireUnlessReady);
      cancelled = true;
      stopAutoRetry?.();
      organizationRecovery.abort();
      // Cleanup speculative pre-connect on unmount or re-run.
      preConnectAbortRef.current?.abort();
      // Spec 4 (D3): a teardown (unmount, sign-out, account switch) invalidates
      // in-flight work, so a run started under the previous identity cannot
      // publish afterwards.
      runGenerationRef.current += 1;
    };
  }, [
    activeRoomRouteId,
    // Refresh role/timezone/organization claims even for the same account.
    // Keep the resolved user while fetching; only the outer Synchronizer resets
    // identity on a different sub, so this cannot publish a provisional ID.
    auth0User,
    getAccessTokenSilently,
    hasLoadedLastRoomIdPref,
    isAuthenticated,
    // MERGE DECISION (ENG-4720 × ENG-4722): `isLoading` is main's and stays — it
    // is read inside the effect. `setLastRoomIdPref` is deliberately NOT here:
    // its identity changes every render, and run() writes that preference, so
    // listing it made the effect re-enter its own write and supersede the run
    // already holding the census and members (a full extra registry round on
    // every studio switch). The body reads the value through `currentRoomIdRef`
    // and writes through the stable `writeLastRoomIdPref`, so nothing inside
    // references the raw setter and the dependency is not merely unnecessary but
    // harmful. Pinned by "does not restart itself when it writes the last-room
    // preference" in synchronizer.active-room-early.test.tsx.
    isLoading,
    produce
  ]);

  // Google has no provisional user. Keep its publishers gated, but expose
  // recovery here: the descendant BootOverlay cannot render before this gate
  // opens, and waiting for the outer startup budget hides Try Again for minutes.
  if (isAuthenticated && !hasStudioUser && bootError) {
    return <BootErrorScreen source='auth-guard' error={bootError} />;
  }

  if (isLoading || (isAuthenticated && !hasStudioUser)) {
    return <Loader />;
  }

  return <>{children}</>;
};

/**
 * `setCurrentUser` function.
 *
 * @description Set the current user.
 */
export const setCurrentUser = (user: User, auth0User?: Auth0User): void => {
  setLastKnownUserId(user.id);
  const orgData = auth0User ? extractOrganizationData(auth0User) : undefined;

  // ENG-4175: prequalification person-properties are NO LONGER persisted here.
  // The segmentation wizard moved POST-auth (routes/agent/setup/post-auth-prequal.tsx);
  // at LOGIN the answers don't exist yet, so this pre-wizard read can no longer be
  // the persistence path. The wizard's completion handler now sets
  // qualification_bucket + the segmentation props on the already-identified user.
  // This identify() call is kept intact so the anonymous→identified attribution/
  // alias linkage (signup-source attribution) is preserved.
  PostHogManager.getInstance().identify(
    user.id,
    {
      email: user.email,
      name: `${user.firstName} ${user.lastName}`,
      firstName: user.firstName,
      lastName: user.lastName,
      mfaEnabled: user.mfaEnabled,
      profilePhotoUrl: user.profilePhotoUrl,
      emailVerified: user.emailVerified,
      role: user.role,
      timezone: user.timezone,
      ...(orgData?.industry && { industry: orgData.industry }),
      ...(orgData?.name && { organizationName: orgData.name }),

      lastVendor: vendorName(),
      lastEnv: config.env
    },
    {
      initialVendor: vendorName(),
      initialEnv: config.env,
      initial_role: user.role,
      ...(orgData?.industry && { initial_industry: orgData.industry }),
      // PostHog set_once is first-ingested-wins: the later identity bridge cannot
      // repair a browser campaign already persisted by this initial identify.
      ...getSignupIdentitySetOnce(auth0User)
    }
  );

  // One browser writer for registration_completed: after identify so the
  // event lands on the merged person. Gated on the Action's first_login
  // claim + a localStorage latch (auth-journey.ts).
  maybeTrackRegistrationCompleted(auth0User, user.id);

  CurrentUser.user = user;
};

export const clearCurrentUser = (): void => {
  CurrentUser.user = undefined;
};

/** `<Synchronizer>` component. */
export const Synchronizer = ({ children }: Pick<Props, 'children'>): ReactElement => {
  const { error: auth0Error, isAuthenticated, isLoading, user: auth0User } = useAuth0();
  const [hasStudioUser, produce] = useDepot((µ) => [!!µ.user, µ.produce], shallow);
  const [studioError, setStudioError] = useState<T_Maybe<Error>>();
  const auth0UserRef = useRef(auth0User);
  auth0UserRef.current = auth0User;
  const [initializedSub, setInitializedSub] = useState<string>();

  useOrgGroupIdentify();
  useStudioGroupIdentify();
  useIntercomSync();

  if (auth0Error) {
    throw fail('734E', auth0Error);
  }

  if (studioError) {
    throw fail('AC93', studioError);
  }

  useEffect(() => {
    const auth0User = auth0UserRef.current;
    // Is the user authenticated?
    if (!isAuthenticated || !auth0User) {
      clearCurrentUser();
      setInitializedSub(undefined);

      produce((draft) => {
        draft.user = undefined;
        draft.account = undefined;
      });

      return;
    }

    // Only database subjects contain an Iris ID. Release this outer loader
    // without inventing a Google user so ProfileSynchronizer can fetch the
    // registry. Its own user gate keeps Network/proxies/LiveKit unmounted.
    // Clearing both stores also retires the previous account on a sub change.
    if (!auth0User.sub?.startsWith('auth0|')) {
      clearCurrentUser();
      produce((draft) => {
        draft.user = undefined;
        draft.account = undefined;
      });
      setInitializedSub(auth0User.sub);
      return;
    }

    // Transform the OAuth user into something we can understand.
    const user = toStudioUser(auth0User);

    if (!user) {
      setStudioError(new StudioError('Unable to decode OAuth2 user.'));
      return;
    }

    // Identify user in PostHog for analytics consistency
    setCurrentUser(user, auth0User);

    produce((draft) => {
      draft.user = user;

      // @fixme - this is a hack until we have real account (AKA organization) records.
      draft.account = { id: castId<Account['id']>(user.id), name: 'Iris Productions' };
    });
    setInitializedSub(auth0User.sub);
  }, [auth0User?.sub, isAuthenticated, produce]);

  // Close the subtree on the render that changes identity, before effects run.
  // A same-sub token/profile refresh keeps the registry user and mounted shell.
  const isIdentityPending = !auth0User?.sub || initializedSub !== auth0User.sub;
  const needsDatabaseUser = auth0User?.sub?.startsWith('auth0|') && !hasStudioUser;
  return (!isElectronApp() && isLoading) ||
    (isAuthenticated && (isIdentityPending || needsDatabaseUser)) ? (
    <Loader />
  ) : (
    <>{children}</>
  );
};
